import { ArrowLeft, ChevronRight, Search, Cog, Fuel, Gauge } from "lucide-react";

const FUEL_STYLE = [
  [/diesel/i, "border-sky-500/70 hover:border-sky-400", "text-sky-400"],
  [/hybride|[ée]lec/i, "border-emerald-500/70 hover:border-emerald-400", "text-emerald-400"],
  [/e85|ethanol|flex/i, "border-hp-yellow/70 hover:border-hp-yellow", "text-hp-yellow"],
  [/essence|petrol/i, "border-red-500/70 hover:border-red-400", "text-red-400"],
];
const fuelStyle = (f = "") => FUEL_STYLE.find(([re]) => re.test(f)) || [null, "border-white/20 hover:border-white/50", "text-neutral-300"];
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function SearchBox({ value, onChange, placeholder }) {
  return (
    <label className="relative block sm:w-80">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-black border border-white/10 pl-9 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-hp-yellow"
        data-testid="config-search" />
    </label>
  );
}

export function Breadcrumb({ items, onBack }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-white/10 bg-hp-carbon" data-testid="config-breadcrumb">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-2 bg-black border border-white/10 hover:border-hp-yellow px-4 py-2 text-sm font-bold transition-colors" data-testid="config-back">
          <ArrowLeft size={16} /> Retour
        </button>
      )}
      <nav className="flex flex-wrap items-center gap-2 text-sm">
        {items.map((it, i) => (
          <span key={it.label + i} className="flex items-center gap-2">
            {i > 0 && <ChevronRight size={14} className="text-neutral-600" />}
            {it.onClick && i < items.length - 1 ? (
              <button onClick={it.onClick} className="text-neutral-400 hover:text-white transition-colors" data-testid={`crumb-${i}`}>{it.label}</button>
            ) : (
              <span className={i === items.length - 1 ? "text-hp-gold font-bold" : "text-neutral-400"}>{it.label}</span>
            )}
          </span>
        ))}
      </nav>
    </div>
  );
}

export function StepHeader({ title, search, setSearch, placeholder }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <h4 className="flex items-center gap-3 font-display text-lg sm:text-xl font-extrabold" data-testid="config-step-title">
        <Gauge size={20} className="text-hp-gold shrink-0" /> {title}
      </h4>
      <SearchBox value={search} onChange={setSearch} placeholder={placeholder} />
    </div>
  );
}

export function TextTiles({ items, onPick, prefix, q }) {
  const list = items.filter((i) => String(i).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid={`${prefix}-grid`}>
      {list.map((it) => (
        <button key={it} onClick={() => onPick(it)}
          className="group flex items-center justify-between gap-2 bg-black border border-white/10 hover:border-hp-yellow hover:bg-hp-yellow/[0.04] px-4 py-5 text-left transition-colors"
          data-testid={`${prefix}-tile-${slug(it)}`}>
          <span className="font-display font-bold text-sm sm:text-base">{it || "Toutes années"}</span>
          <ChevronRight size={16} className="text-neutral-600 group-hover:text-hp-yellow transition-transform group-hover:translate-x-0.5 shrink-0" />
        </button>
      ))}
      {!list.length && <p className="col-span-full text-sm text-neutral-500 py-6">Aucun résultat.</p>}
    </div>
  );
}

export function EngineCards({ engines, onPick, q }) {
  const list = engines.filter((e) => `${e.engine} ${e.fuel} ${e.origin_hp}`.toLowerCase().includes(q.trim().toLowerCase()));
  const groups = list.reduce((acc, e) => ({ ...acc, [e.fuel || "Autre"]: [...(acc[e.fuel || "Autre"] || []), e] }), {});
  return (
    <div className="space-y-8" data-testid="engine-groups">
      {Object.entries(groups).map(([fuel, items]) => (
        <div key={fuel}>
          <h5 className="flex items-center gap-2 font-display text-lg font-extrabold mb-3"><Fuel size={18} className="text-hp-gold" /> {fuel}</h5>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((e) => {
              const [, border, color] = fuelStyle(e.fuel);
              return (
                <button key={e.id} onClick={() => onPick(e.id)}
                  className={`group text-left bg-black border-2 ${border} p-5 transition-colors`} data-testid={`engine-card-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex items-center gap-2 font-display font-extrabold text-base"><Cog size={17} className="text-hp-gold shrink-0" /> {e.engine}</p>
                    <ChevronRight size={16} className="text-neutral-500 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </div>
                  <p className={`flex items-center gap-2 text-sm font-bold mt-2 ${color}`}><Fuel size={14} /> {e.fuel}</p>
                  <p className="flex items-center gap-2 text-sm text-neutral-400 mt-2"><Gauge size={14} className="text-hp-gold" /> {e.origin_hp ? `${e.origin_hp} ch` : "N.C."}
                    {e.stage1_hp && <span className="ml-auto text-xs font-bold text-hp-yellow">Stage 1 : {e.stage1_hp} ch</span>}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!list.length && <p className="text-sm text-neutral-500 py-6">Aucune motorisation trouvée.</p>}
    </div>
  );
}
